import { useEffect, useMemo, type ReactNode } from 'react'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { prefixer } from 'stylis'
import rtlPlugin from 'stylis-plugin-rtl'
import { buildTheme } from './theme'
import { useLocale } from '../i18n/LocaleProvider'

/**
 * Wires text direction through Emotion, MUI and the document at once.
 *
 * <p>Right-to-left is not a CSS switch you can bolt on later. Emotion needs a cache with the
 * RTL stylis plugin so that every `margin-left` MUI emits is flipped to `margin-right`; MUI
 * needs `direction` in the theme so its own logic (drawer anchors, icon rotation, slide
 * transitions) agrees; and the html element needs `dir` so the browser lays out text runs and
 * scrollbars correctly. Miss any one and Hebrew renders half-mirrored, which looks worse than
 * not supporting it at all.
 *
 * <p>Two caches are built once and kept. Swapping the Emotion cache re-renders every styled
 * component, so this must not happen on any render other than an actual language change.
 */

const ltrCache = createCache({ key: 'mui', stylisPlugins: [prefixer] })
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] })

export function DirectionProvider({ children }: { children: ReactNode }) {
  const { locale, direction } = useLocale()

  const theme = useMemo(() => buildTheme(direction), [direction])
  const cache = direction === 'rtl' ? rtlCache : ltrCache

  useEffect(() => {
    const html = document.documentElement
    html.setAttribute('dir', direction)
    html.setAttribute('lang', locale)
  }, [direction, locale])

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CacheProvider>
  )
}
